'use client';

import { useState } from 'react';
import { ExternalService } from '@/lib/data/tool-privacy';

interface PrivacyNoticeProps {
  /** Type of privacy: client-only (green) or external-api (amber) */
  variant: 'client-only' | 'external-api';
  /** External services for external-api variant */
  externalServices?: ExternalService[];
  /** Optional custom class name */
  className?: string;
}

/**
 * Privacy Notice Component
 *
 * Displays transparent privacy information for tools.
 * - client-only: Green badge showing all processing happens in browser
 * - external-api: Amber expandable notice listing external services and data sent
 */
export default function PrivacyNotice({
  variant,
  externalServices = [],
  className = '',
}: PrivacyNoticeProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (variant === 'client-only') {
    return (
      <div
        className={`bg-green-50 border border-green-200 rounded-lg p-4 ${className}`}
      >
        <div className="flex items-center gap-3">
          {/* Shield/Lock Icon */}
          <svg
            className="w-5 h-5 text-green-600 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
              clipRule="evenodd"
            />
          </svg>
          <div>
            <div className="text-sm font-semibold text-green-800">
              100% Private - Runs Entirely in Your Browser
            </div>
            <div className="text-xs text-green-700 mt-0.5">
              No data is sent to any server. All processing happens locally on your device.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // external-api variant
  return (
    <div
      className={`bg-amber-50 border border-amber-200 rounded-lg overflow-hidden ${className}`}
    >
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center justify-between text-left hover:bg-amber-100/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {/* Info Icon */}
          <svg
            className="w-5 h-5 text-amber-600 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <div>
            <div className="text-sm font-semibold text-amber-800">
              This Tool Uses External Services
            </div>
            <div className="text-xs text-amber-700 mt-0.5">
              Click to see what data is sent and where
            </div>
          </div>
        </div>
        <svg
          className={`w-5 h-5 text-amber-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-3">
          <div className="border-t border-amber-200 pt-3">
            <div className="text-xs font-medium text-amber-800 uppercase tracking-wide mb-2">
              External Services Used
            </div>
            <div className="space-y-3">
              {externalServices.map((service, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg p-3 border border-amber-100"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 text-sm">
                        {service.name}
                      </div>
                      <div className="text-xs text-gray-500 font-mono mt-0.5">
                        {service.url}
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 text-sm text-gray-700">
                    <span className="font-medium">Data sent:</span> {service.dataSent}
                  </div>
                  {service.privacyNote && (
                    <div className="mt-1.5 text-xs text-green-700 bg-green-50 rounded px-2 py-1 inline-block">
                      {service.privacyNote}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Our Commitment */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
            <div className="text-xs font-medium text-blue-800 mb-1">
              Our Privacy Commitment
            </div>
            <ul className="text-xs text-blue-700 space-y-0.5">
              <li>• We do not log or store the data you enter</li>
              <li>• Queries are made server-side to protect your IP from third parties</li>
              <li>• No tracking or analytics on tool inputs</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
